import type { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'
import type { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes'

import {
  failedToUpgradeResult,
  monitorAccessDisabledResult,
  timedOutResult,
} from '@racepointenergy/swc/scs/client/dealer-websocket-machine/endpoints/results'
import { upgradeDealerInterface } from '@racepointenergy/swc/scs/upgrade-dealer-interface'
import { readRmsHomesHome } from '@racepointenergy/swc/scs/read-rms-homes-home'
import { writeDealerWebSocket } from '@racepointenergy/swc/scs/client/dealer-websocket-machine/actions'
import { DealerReadyState } from '@racepointenergy/swc/scs/client/dealer-websocket-machine/types'

import type { HomeId } from '@racepointenergy/swc/scs/types/uid'

import { api } from '@racepointenergy/swc/client/api'
import { clientMiddleware } from '@racepointenergy/swc/client/middleware'
import { serializeBaseQueryError } from '@racepointenergy/swc/client/utils/serialize-error'

export interface DealerConfigurePowerCyclePduArguments {
  id: HomeId
  loadName: string
  value?: number
  toggle?: boolean
}

export type DealerConfigurePowerCyclePduResult = null

/** @internal */
export const injectedApi = api.injectEndpoints({
  endpoints(builder) {
    return {
      dealerConfigurePowerCyclePdu: builder.mutation<
        DealerConfigurePowerCyclePduResult,
        DealerConfigurePowerCyclePduArguments
      >({
        async queryFn(queryArguments, { dispatch }) {
          const {
            id: homeId,
            loadName,
            value = 0,
            toggle = false,
          } = queryArguments
          const upgradeSubscription = dispatch(
            upgradeDealerInterface.initiate({ homeId }, { subscribe: true })
          )
          const selectReadyState = upgradeDealerInterface.select({ homeId })

          try {
            // upgradeSubscription already subscribes to readRmsHomesHome
            const home = await dispatch(
              readRmsHomesHome.initiate({ homeId }, { subscribe: false })
            ).unwrap()
            if (home.monitorAccessEnabled !== true) {
              return monitorAccessDisabledResult
            }
          } catch (upgradeError) {
            return failedToUpgradeResult
          }
          const t = toggle ? { toggleTime: 5 } : {}
          const writeData = JSON.stringify({
            URI: 'dis/dashboard/request',
            messages: [
              {
                request: 'EnergyCommand',
                requestArgs: {
                  commandName: 'setEnergyOnlyLoad',
                  loadName,
                  value,
                  ...t,
                },
              },
            ],
          })

          return new Promise<
            QueryReturnValue<
              DealerConfigurePowerCyclePduResult,
              FetchBaseQueryError,
              unknown
            >
          >((resolve) => {
            const unsubscribeWriter = clientMiddleware.startListening({
              effect: function writeWhenAcknowledged(anyAction, listenerApi) {
                listenerApi.cancelActiveListeners()
                listenerApi.unsubscribe()
                // necessary to fork so task can be cancelled
                return listenerApi
                  .fork(() => {
                    dispatch(
                      writeDealerWebSocket({
                        data: writeData,
                        to: homeId,
                      })
                    )
                  })
                  .result.then(() => {
                    resolve({ data: null })
                  })
                  .catch((reason) => {
                    resolve(serializeBaseQueryError(reason))
                  })
                  .finally(() => {
                    clearTimeout(timeoutId)
                  })
              },
              predicate: function readyStateIsAcknowledged(
                action,
                currentState
              ) {
                const readyState = selectReadyState(currentState).data
                return (
                  // redux bug: filter action type to ignore thunks
                  typeof action?.type === 'string' &&
                  readyState === DealerReadyState.acknowledged
                )
              },
            })

            const timeoutId = setTimeout(() => {
              resolve(timedOutResult)
              unsubscribeWriter()
            }, 1000 * 18)
          }).finally(() => {
            upgradeSubscription.unsubscribe()
          })
        },
      }),
    }
  },
})

export const dealerConfigurePowerCyclePdu =
  injectedApi.endpoints.dealerConfigurePowerCyclePdu
